import { safeSetDoc } from '../utils';
import { useState, useEffect, useRef, useCallback } from 'react';
import { AVATARS } from '../constants';
import Avatar from './Avatar';
import {
  Undo,
  Trash2,
  Clock,
  Upload,
  RefreshCw,
  Copy,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  Calendar,
  HelpCircle,
  Star,
  ListTodo,
  Plane,
  Bed,
  Users,
  X,
  Plus,
  Check,
  Search,
  UserPlus,
  UserCheck,
  UserMinus,
} from 'lucide-react';
import CitySearch from './CitySearch';
import { auth, db } from '../firebase';
import { GoogleAuthProvider, linkWithPopup, signInWithPopup } from 'firebase/auth';

const ProfileSettings = ({
  initialName,
  initialBio,
  initialMotivation,
  initialAvatar,
  initialCities,
  saveUserProfile,
  handleSignOut,
  user,
  deletedChannels,
  handleRestoreChannel,
  handlePurgeChannel,
  handleImportProject,
  isImporting,
  profile,
  projects = [],
  dms = [],
  participantNames = {},
  allUsers = [],
  googleCalendars = [],
}) => {
  const [name, setName] = useState(initialName || '');
  const [bio, setBio] = useState(initialBio || '');
  const [birthYear, setBirthYear] = useState(profile?.birthYear || '');
  const [birthdayMonth, setBirthdayMonth] = useState(profile?.birthday ? profile.birthday.split('-')[0] : '');
  const [birthdayDay, setBirthdayDay] = useState(profile?.birthday ? profile.birthday.split('-')[1] : '');
  const [motivation, setMotivation] = useState(initialMotivation || '');
  const [selectedAvatar, setSelectedAvatar] = useState(initialAvatar || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isURLTreeOpen, setIsURLTreeOpen] = useState(false);
  const [listenBrainzUsername, setListenBrainzUsername] = useState(profile?.listenBrainzUsername || '');
  
  const [friendsList, setFriendsList] = useState(profile?.friends || []);
  const [friendSearch, setFriendSearch] = useState('');
  const [socialLinks, setSocialLinks] = useState(profile?.socialLinks || []);

  useEffect(() => {
    if (profile?.friends && JSON.stringify(friendsList) !== JSON.stringify(profile.friends)) {
      setFriendsList(profile.friends);
    }
  }, [profile?.friends]);

  const [cities, setCities] = useState(() => {
    const initial =
      initialCities && initialCities.length > 0 && initialCities[0]?.name
        ? initialCities
        : [{ name: 'Dallas, TX', lat: 32.7767, lng: -96.797, timezone: 'America/Chicago' }];
    const filled = [...initial];
    if (filled.length === 0) {
      filled.push({ name: 'Dallas, TX', lat: 32.7767, lng: -96.797, timezone: 'America/Chicago' });
    }
    return filled.slice(0, 1);
  });

  const lastInitialCitiesRef = useRef(initialCities);

  useEffect(() => {
    if (JSON.stringify(initialCities) !== JSON.stringify(lastInitialCitiesRef.current)) {
      lastInitialCitiesRef.current = initialCities;
      const initial =
        initialCities && initialCities.length > 0 && initialCities[0]?.name
          ? initialCities
          : [{ name: 'Dallas, TX', lat: 32.7767, lng: -96.797, timezone: 'America/Chicago' }];
      const filled = [...initial];
      if (filled.length === 0) {
        filled.push({ name: 'Dallas, TX', lat: 32.7767, lng: -96.797, timezone: 'America/Chicago' });
      }
      setCities(filled.slice(0, 1));
    }
  }, [initialCities]);

  useEffect(() => {
    setName(initialName || '');
  }, [initialName]);

  useEffect(() => {
    setBio(initialBio || '');
  }, [initialBio]);

  useEffect(() => {
    setBirthYear(profile?.birthYear || '');
    setBirthdayMonth(profile?.birthday ? profile.birthday.split('-')[0] : '');
    setBirthdayDay(profile?.birthday ? profile.birthday.split('-')[1] : '');
  }, [profile?.birthYear, profile?.birthday]);

  useEffect(() => {
    setMotivation(initialMotivation || '');
  }, [initialMotivation]);

  useEffect(() => {
    setListenBrainzUsername(profile?.listenBrainzUsername || '');
  }, [profile?.listenBrainzUsername]);

  useEffect(() => {
    setSelectedAvatar(initialAvatar || '');
  }, [initialAvatar]);

  const hasChanges =
    name !== (initialName || '') ||
    bio !== (initialBio || '') ||
    birthYear !== (profile?.birthYear || '') ||
    birthdayMonth !== (profile?.birthday ? profile.birthday.split('-')[0] : '') ||
    birthdayDay !== (profile?.birthday ? profile.birthday.split('-')[1] : '') ||
    motivation !== (initialMotivation || '') ||
    listenBrainzUsername !== (profile?.listenBrainzUsername || '') ||
    selectedAvatar !== (initialAvatar || '') ||
    JSON.stringify(cities.filter((c) => c.name)) !== JSON.stringify(initialCities || []) ||
    JSON.stringify(friendsList) !== JSON.stringify(profile?.friends || []) ||
    JSON.stringify(socialLinks) !== JSON.stringify(profile?.socialLinks || []);

  const handleCityChange = useCallback((index, newCity) => {
    setCities((prev) => {
      const next = [...prev];
      next[index] = newCity;
      return next;
    });
  }, []);

  const handleSaveAll = async () => {
    setIsSaving(true);
    const birthday = (birthdayMonth && birthdayDay) ? `${birthdayMonth}-${birthdayDay}` : null;
    try {
      await saveUserProfile({
        name,
        bio,
        birthYear: birthYear || null,
        birthday: birthday,
        motivationalMessage: motivation,
        listenBrainzUsername: listenBrainzUsername,
        avatar: selectedAvatar,
        cities: cities.filter((c) => c.name), // Save only filled ones
        friends: friendsList,
        socialLinks: socialLinks.filter(l => l.url.trim() !== ''),
      });
      setTimeout(() => setIsSaving(false), 1000);
    } catch {
      setIsSaving(false);
    }
  };

  const handleCopy = (e, url) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    alert('URL copied to clipboard!');
  };

  const baseUrl = window.location.origin;

  if (!initialCities && user) {
    return (
      <div className="flex-1 flex items-center justify-center p-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900"></div>
      </div>
    );
  }

  return (
    <section className="mb-10 text-left">
      <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">Profile</h2>
      <div className="flex flex-col gap-4">
        <div>
          <label htmlFor="profile-display-name" className="text-sm text-zinc-600 block mb-1 font-bold">
            Username
          </label>
          <input
            id="profile-display-name"
            data-testid="profile-display-name"
            name="profile-display-name"
            type="text"
            maxLength={20}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full max-w-md bg-zinc-50 border-2 border-zinc-200 rounded px-3 py-2 text-sm font-bold focus:border-zinc-400 focus:outline-none transition-colors"
          />
        </div>
        <div>
          <label htmlFor="profile-bio" className="text-sm text-zinc-600 block mb-1 font-bold">
            Bio
          </label>
          <textarea
            id="profile-bio"
            data-testid="profile-bio"
            name="profile-bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell us about yourself..."
            className="w-full max-w-md bg-zinc-50 border-2 border-zinc-200 rounded px-3 py-2 text-sm font-bold focus:border-zinc-400 focus:outline-none transition-colors min-h-[100px] resize-none"
          />
        </div>
        <div className="flex flex-col gap-4 max-w-md">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label htmlFor="profile-birth-year" className="text-sm text-zinc-600 block mb-1 font-bold">
                Birth Year
              </label>
              <input
                id="profile-birth-year"
                type="number"
                placeholder="e.g. 1990"
                value={birthYear}
                onChange={(e) => setBirthYear(e.target.value)}
                className="w-full bg-zinc-50 border-2 border-zinc-200 rounded px-3 py-2 text-sm font-bold focus:border-zinc-400 focus:outline-none transition-colors tabular-nums"
              />
            </div>
            <div className="flex-[2]">
              <label htmlFor="profile-birthday-month" className="text-sm text-zinc-600 block mb-1 font-bold">
                Birthday
              </label>
              <div className="flex gap-2">
                <select
                  id="profile-birthday-month"
                  value={birthdayMonth}
                  onChange={(e) => setBirthdayMonth(e.target.value)}
                  className="flex-1 bg-zinc-50 border-2 border-zinc-200 rounded px-3 py-2 text-sm font-bold focus:border-zinc-400 focus:outline-none transition-colors"
                >
                  <option value="">Month</option>
                  {Array.from({ length: 12 }, (_, i) => {
                    const m = String(i + 1).padStart(2, '0');
                    return <option key={m} value={m}>{new Date(2000, i).toLocaleString('default', { month: 'long' })}</option>;
                  })}
                </select>
                <select
                  id="profile-birthday-day"
                  value={birthdayDay}
                  onChange={(e) => setBirthdayDay(e.target.value)}
                  className="w-24 bg-zinc-50 border-2 border-zinc-200 rounded px-3 py-2 text-sm font-bold focus:border-zinc-400 focus:outline-none transition-colors"
                >
                  <option value="">Day</option>
                  {Array.from({ length: 31 }, (_, i) => {
                    const d = String(i + 1).padStart(2, '0');
                    return <option key={d} value={d}>{d}</option>;
                  })}
                </select>
              </div>
            </div>
          </div>
          <p className="text-[10px] text-zinc-400 italic font-medium -mt-2">
            This will show &quot;My Birthday&quot; on the calendar for years after you were born.
          </p>
        </div>
        <div>
          <label
            htmlFor="profile-motivational-message"
            className="text-sm text-zinc-600 block mb-1 font-bold"
          >
            Motivational Message
          </label>
          <input
            id="profile-motivational-message"
            data-testid="profile-motivational-message"
            name="profile-motivational-message"
            type="text"
            maxLength={60}
            value={motivation}
            onChange={(e) => setMotivation(e.target.value)}
            className="w-full max-w-md bg-zinc-50 border-2 border-zinc-200 rounded px-3 py-2 text-sm font-bold focus:border-zinc-400 focus:outline-none transition-colors"
          />
        </div>

        <div className="mt-2 mb-4">
          <label
            htmlFor="profile-listenbrainz-username"
            className="text-sm text-zinc-600 block mb-1 font-bold flex items-center gap-2"
          >
            ListenBrainz Username
            <span className="text-[10px] text-zinc-400 font-medium">(optional)</span>
          </label>
          <input
            id="profile-listenbrainz-username"
            data-testid="profile-listenbrainz-username"
            name="profile-listenbrainz-username"
            type="text"
            value={listenBrainzUsername}
            onChange={(e) => setListenBrainzUsername(e.target.value)}
            placeholder="e.g. your_username"
            className="w-full max-w-md bg-zinc-50 border-2 border-zinc-200 rounded px-3 py-2 text-sm font-bold focus:border-zinc-400 focus:outline-none transition-colors"
          />
        </div>

        <div className="mt-2 mb-4">
          <label className="text-sm text-zinc-600 block mb-1 font-bold flex items-center gap-2">
            Social Media / Link-in-Bio
            <span className="text-[10px] text-zinc-400 font-medium">(Public on your profile)</span>
          </label>
          <div className="flex flex-col gap-2 max-w-md">
            {socialLinks.map((link, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input
                  type="text"
                  value={link.title}
                  onChange={(e) => {
                    const newLinks = [...socialLinks];
                    newLinks[idx].title = e.target.value;
                    setSocialLinks(newLinks);
                  }}
                  placeholder="Platform (e.g. Instagram)"
                  className="flex-1 bg-zinc-50 border-2 border-zinc-200 rounded px-3 py-2 text-sm font-bold focus:border-zinc-400 focus:outline-none transition-colors"
                />
                <input
                  type="text"
                  value={link.url}
                  onChange={(e) => {
                    const newLinks = [...socialLinks];
                    newLinks[idx].url = e.target.value;
                    setSocialLinks(newLinks);
                  }}
                  placeholder="https://..."
                  className="flex-[2] bg-zinc-50 border-2 border-zinc-200 rounded px-3 py-2 text-sm font-bold focus:border-zinc-400 focus:outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newLinks = socialLinks.filter((_, i) => i !== idx);
                    setSocialLinks(newLinks);
                  }}
                  className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
                >
                  <X size={16} strokeWidth={3} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setSocialLinks([...socialLinks, { title: '', url: '' }])}
              className="mt-1 flex items-center justify-center gap-2 px-3 py-2 bg-zinc-100 text-zinc-600 rounded text-xs font-black uppercase tracking-widest hover:bg-zinc-200 transition-colors"
            >
              <Plus size={14} /> Add Link
            </button>
          </div>
        </div>

        <div className="mt-2 mb-4">
          <label className="text-[10px] text-zinc-400 block mb-3 font-black uppercase tracking-[0.2em]">
            Pick your vibe
          </label>
          <div className="flex flex-wrap gap-3 max-w-md">
            {user?.photoURL && (
              <button
                type="button"
                onClick={() => setSelectedAvatar(user.photoURL)}
                className={`p-1 rounded-xl hover:bg-zinc-100 transition-all active:scale-90 flex-shrink-0 ${selectedAvatar === user.photoURL ? 'bg-zinc-100 ring-2 ring-zinc-900/10 scale-110 shadow-sm' : 'opacity-40 hover:opacity-100'}`}
                title="Use Google Profile Photo"
              >
                <img
                  src={user.photoURL}
                  alt="Google Profile"
                  className="w-8 h-8 rounded-lg object-cover"
                  referrerPolicy="no-referrer"
                />
              </button>
            )}

            {AVATARS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => setSelectedAvatar(emoji)}
                className={`text-2xl p-2 rounded-xl hover:bg-zinc-100 transition-all active:scale-90 flex-shrink-0 ${selectedAvatar === emoji ? 'bg-zinc-100 ring-2 ring-zinc-900/10 scale-110 shadow-sm' : 'opacity-40 hover:opacity-100'}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <label className="text-sm text-zinc-600 block mb-2 font-bold">Home Address:</label>
          <div className="flex flex-col gap-2">
            {cities.map((city, idx) => (
              <CitySearch
                key={idx}
                index={idx}
                city={city}
                onCityChange={handleCityChange}
                className="max-w-md"
                types={['address']}
              />
            ))}
          </div>
        </div>

        <div className="pt-2 flex items-center gap-4">
          <button
            type="button"
            onClick={handleSaveAll}
            disabled={isSaving}
            className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-sm ${
              isSaving
                ? 'bg-green-500 text-white animate-pulse'
                : hasChanges
                  ? 'bg-blue-600 text-white hover:bg-blue-700 ring-4 ring-blue-100 active:scale-95'
                  : 'bg-zinc-900 text-white hover:bg-black active:scale-95'
            }`}
          >
            {isSaving ? 'Saved!' : 'Save Profile'}
          </button>

          {hasChanges && !isSaving && (
            <div className="flex items-center gap-2 animate-bounce-horizontal text-blue-600">
              <ArrowLeft size={20} strokeWidth={3} />
              <span className="text-[10px] font-black uppercase tracking-widest">Unsaved Changes</span>
            </div>
          )}
        </div>

        <div className="mt-12 pt-8 border-t border-zinc-100">
          <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-6">Account</h2>
          <div className="flex flex-col items-start gap-4 max-w-md">
            <div className="w-full flex items-center gap-3 bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm text-left">
              <Avatar src={initialAvatar} size={40} className="rounded-xl" />
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-bold text-zinc-800 truncate">
                  {user?.email || 'Guest User'}
                </span>
                <span className="text-[10px] text-green-500 font-bold uppercase tracking-tight">
                  Active Session
                </span>
              </div>
            </div>
            <button
              type="button"
              data-testid="sign-out-btn"
              onClick={handleSignOut}
              className="px-6 py-2.5 bg-red-50 text-red-600 text-[10px] font-black uppercase tracking-[0.2em] rounded-xl hover:bg-red-100 transition-all border border-red-100 flex items-center gap-2 active:scale-95"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Friends Section */}
        <div className="mt-12 pt-8 border-t border-zinc-100">
          <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-6 flex items-center gap-2">
            <Users size={18} /> Friends
          </h2>
          <div className="flex flex-col gap-6 max-w-md">
            
            {/* Find Friends Search */}
            <div>
              <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mb-2">
                Find Friends
              </p>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  value={friendSearch}
                  onChange={(e) => setFriendSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full bg-zinc-50 border-2 border-zinc-200 rounded-xl pl-10 pr-3 py-2 text-sm font-bold focus:border-blue-400 focus:outline-none transition-colors"
                />
              </div>
            </div>

            {(() => {
              const myEmail = profile?.email?.toLowerCase();
              if (!myEmail) return <p className="text-sm text-zinc-400">Please set an email to use friends.</p>;

              const allOtherUsers = (allUsers || []).filter(u => u.email && u.email.toLowerCase() !== myEmail);
              
              // Categorize users
              const requests = [];
              const pending = [];
              const mutuals = [];
              const searchResults = [];

              allOtherUsers.forEach(u => {
                const theirEmail = u.email.toLowerCase();
                const theirFriends = u.friends || [];
                const iRequestedThem = friendsList.includes(theirEmail) && !theirFriends.includes(myEmail);
                const theyRequestedMe = !friendsList.includes(theirEmail) && theirFriends.includes(myEmail);
                const isMutual = friendsList.includes(theirEmail) && theirFriends.includes(myEmail);

                const userObj = { ...u, theirEmail, isMutual, iRequestedThem, theyRequestedMe };
                
                if (theyRequestedMe) requests.push(userObj);
                else if (isMutual) mutuals.push(userObj);
                else if (iRequestedThem) pending.push(userObj);
                
                // If they are searching, add to search results if it matches and isn't mutual
                if (friendSearch.length >= 2) {
                  const searchStr = friendSearch.toLowerCase();
                  if ((u.name?.toLowerCase().includes(searchStr) || theirEmail.includes(searchStr)) && !isMutual) {
                    // Only add to search results if not already in requests/pending to avoid duplicates
                    if (!theyRequestedMe && !iRequestedThem) {
                      searchResults.push(userObj);
                    }
                  }
                }
              });

              return (
                <div className="flex flex-col gap-6">
                  {/* Friend Requests (Inbound) */}
                  {requests.length > 0 && (
                    <div>
                      <p className="text-[10px] text-blue-500 font-bold uppercase tracking-widest mb-2 flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                        Friend Requests
                      </p>
                      <div className="flex flex-col gap-2">
                        {requests.map(u => (
                          <div key={u.id} className="flex items-center justify-between bg-blue-50/50 border border-blue-100 px-3 py-2 rounded-xl">
                            <div className="flex items-center gap-3">
                              <Avatar seed={u.avatar} size={28} className="flex-shrink-0" />
                              <div className="flex flex-col">
                                <span className="text-sm font-bold text-zinc-800 leading-tight">{u.name}</span>
                                <span className="text-xs text-zinc-500 leading-tight">{u.theirEmail}</span>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                const newList = [...friendsList, u.theirEmail];
                                setFriendsList(newList);
                                saveUserProfile({ friends: newList });
                                setFriendSearch('');
                              }}
                              className="px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                            >
                              <UserCheck size={14} /> Accept
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Search Results */}
                  {friendSearch.length >= 2 && searchResults.length > 0 && (
                    <div>
                      <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mb-2">
                        Search Results
                      </p>
                      <div className="flex flex-col gap-2">
                        {searchResults.map(u => (
                          <div key={u.id} className="flex items-center justify-between bg-white border border-zinc-200 px-3 py-2 rounded-xl">
                            <div className="flex items-center gap-3">
                              <Avatar seed={u.avatar} size={28} className="flex-shrink-0" />
                              <div className="flex flex-col">
                                <span className="text-sm font-bold text-zinc-800 leading-tight">{u.name}</span>
                                <span className="text-xs text-zinc-500 leading-tight">{u.theirEmail}</span>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                const newList = [...friendsList, u.theirEmail];
                                setFriendsList(newList);
                                saveUserProfile({ friends: newList });
                                setFriendSearch('');
                              }}
                              className="px-3 py-1.5 bg-zinc-100 text-zinc-700 text-xs font-bold rounded-lg hover:bg-zinc-200 transition-colors flex items-center gap-1"
                            >
                              <UserPlus size={14} /> Add
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pending Outbound Requests */}
                  {pending.length > 0 && !friendSearch && (
                    <div>
                      <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mb-2">
                        Sent Requests
                      </p>
                      <div className="flex flex-col gap-2">
                        {pending.map(u => (
                          <div key={u.id} className="flex items-center justify-between bg-zinc-50 border border-zinc-200 px-3 py-2 rounded-xl opacity-75">
                            <div className="flex items-center gap-3">
                              <Avatar seed={u.avatar} size={28} className="flex-shrink-0 grayscale opacity-75" />
                              <div className="flex flex-col">
                                <span className="text-sm font-bold text-zinc-700 leading-tight">{u.name}</span>
                                <span className="text-xs text-zinc-400 leading-tight">Pending Approval</span>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                if (!window.confirm("Cancel this friend request? This can't be undone.")) return;
                                const newList = friendsList.filter(e => e !== u.theirEmail);
                                setFriendsList(newList);
                                saveUserProfile({ friends: newList });
                              }}
                              className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-zinc-100 rounded-lg transition-colors"
                              title="Cancel Request"
                            >
                              <X size={14} strokeWidth={3} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mutual Friends */}
                  {mutuals.length > 0 && !friendSearch && (
                    <div>
                      <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-widest mb-2">
                        My Friends
                      </p>
                      <div className="flex flex-col gap-2">
                        {mutuals.map(u => (
                          <div key={u.id} className="flex items-center justify-between bg-emerald-50/30 border border-emerald-100/50 px-3 py-2 rounded-xl">
                            <div className="flex items-center gap-3">
                              <Avatar seed={u.avatar} size={28} className="flex-shrink-0" />
                              <div className="flex flex-col">
                                <span className="text-sm font-bold text-zinc-800 leading-tight">{u.name}</span>
                                <span className="text-xs text-zinc-500 leading-tight">{u.theirEmail}</span>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                if (window.confirm(`Are you sure you want to remove ${u.name} from your friends list?`)) {
                                  const newList = friendsList.filter(e => e !== u.theirEmail);
                                  setFriendsList(newList);
                                  saveUserProfile({ friends: newList });
                                }
                              }}
                              className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-zinc-100 rounded-lg transition-colors"
                              title="Remove Friend"
                            >
                              <UserMinus size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {mutuals.length === 0 && pending.length === 0 && requests.length === 0 && !friendSearch && (
                    <div className="text-center py-6 bg-zinc-50 border border-zinc-200 rounded-xl border-dashed">
                      <p className="text-xs font-medium text-zinc-500">You don't have any friends yet.</p>
                      <p className="text-[10px] text-zinc-400 mt-1">Use the search bar above to find people.</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* URL TREE SECTION */}
        {(projects.length > 0 || dms.length > 0) && (
          <div className="mt-12 pt-8 border-t border-zinc-100">
            <button
              onClick={() => setIsURLTreeOpen(!isURLTreeOpen)}
              className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-zinc-400 mb-6 hover:text-zinc-600 transition-colors"
            >
              {isURLTreeOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              URL Tree
            </button>

            {isURLTreeOpen && (
              <div className="flex flex-col gap-8 pl-2 pb-8">
                <div className="flex flex-col gap-3">
                  <h3 className="text-[10px] font-black uppercase text-zinc-400 tracking-[0.2em]">
                    Global
                  </h3>
                  <div className="flex flex-col gap-4 border-l-2 border-zinc-100 pl-4">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2 group">
                        <button
                          onClick={(e) => handleCopy(e, `${baseUrl}/social`)}
                          className="text-zinc-300 hover:text-zinc-900 transition-colors"
                          title="Copy URL"
                        >
                          <Copy size={12} />
                        </button>
                        <a
                          href={`/social`}
                          className="text-[11px] font-bold text-zinc-500 hover:text-zinc-900 transition-colors"
                        >
                          Social Media Feed
                        </a>
                      </div>
                      <div className="flex items-center gap-2 group">
                        <button
                          onClick={(e) => handleCopy(e, `${baseUrl}/eventchanges`)}
                          className="text-zinc-300 hover:text-zinc-900 transition-colors"
                          title="Copy URL"
                        >
                          <Copy size={12} />
                        </button>
                        <a
                          href={`/eventchanges`}
                          className="text-[11px] font-bold text-zinc-500 hover:text-zinc-900 transition-colors"
                        >
                          Event Changes Log
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <h3 className="text-[10px] font-black uppercase text-zinc-400 tracking-[0.2em]">
                    Projects
                  </h3>
                  <div className="flex flex-col gap-4 border-l-2 border-zinc-100 pl-4">
                    {(projects || []).map((p) => (
                      <div key={p.id} className="flex flex-col gap-1.5">
                        <span className="text-xs font-black text-zinc-900 uppercase tracking-tight">
                          {p.name}
                        </span>
                        <div className="flex flex-wrap gap-x-6 gap-y-2">
                          {[
                            { label: 'Chat', path: `/p/${p.id}/chat` },
                            { label: 'Tasks', path: `/p/${p.id}/tasks` },
                            { label: 'Notes', path: `/p/${p.id}/notes` },
                          ].map((link) => (
                            <div key={link.path} className="flex items-center gap-2 group">
                              <button
                                onClick={(e) => handleCopy(e, `${baseUrl}${link.path}`)}
                                className="text-zinc-300 hover:text-zinc-900 transition-colors"
                                title="Copy URL"
                              >
                                <Copy size={12} />
                              </button>
                              <a
                                href={link.path}
                                className="text-[11px] font-bold text-zinc-500 hover:text-zinc-900 transition-colors"
                              >
                                {link.label}
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <h3 className="text-[10px] font-black uppercase text-zinc-400 tracking-[0.2em]">
                    Private Messages
                  </h3>
                  <div className="flex flex-col gap-3 border-l-2 border-zinc-100 pl-4">
                    {(dms || []).filter(dm => {
                      const otherId = dm.participants?.find((id) => id !== user?.uid);
                      const targetUser = allUsers.find(u => u.id === otherId);
                      if (!targetUser) return false;
                      const theirEmail = targetUser.email?.toLowerCase();
                      const theirFriends = targetUser.friends || [];
                      const myFriends = profile?.friends || [];
                      const myEmail = user?.email?.toLowerCase();
                      return myFriends.includes(theirEmail) && theirFriends.includes(myEmail);
                    }).map((dm) => {
                      const otherId = dm.participants?.find((id) => id !== user?.uid);
                      const otherUser = participantNames[otherId] || otherId || 'Unknown User';
                      return (
                        <div key={dm.id} className="flex items-center gap-2 group">
                          <button
                            onClick={(e) => handleCopy(e, `${baseUrl}/p/${dm.id}/chat`)}
                            className="text-zinc-300 hover:text-zinc-900 transition-colors"
                            title="Copy URL"
                          >
                            <Copy size={12} />
                          </button>
                          <a
                            href={`/p/${dm.id}/chat`}
                            className="text-[11px] font-bold text-zinc-500 hover:text-zinc-900 transition-colors"
                          >
                            DM with {otherUser}
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-zinc-100">
          <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-6">
            Backup & Recovery
          </h2>
          <div className="max-w-md">
            <input
              type="file"
              id="import-project-input"
              className="hidden"
              accept=".json"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  handleImportProject(e.target.files[0]);
                  e.target.value = ''; // Reset
                }
              }}
            />
            <button
              type="button"
              disabled={isImporting}
              onClick={() => document.getElementById('import-project-input')?.click()}
              className={`w-full py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-sm flex items-center justify-center gap-2 ${isImporting ? 'bg-zinc-100 text-zinc-400' : 'bg-white text-zinc-900 border-2 border-zinc-900 hover:bg-zinc-900 hover:text-white active:scale-95'}`}
            >
              {isImporting ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Import Project (.json)
                </>
              )}
            </button>
            <p className="mt-2 text-[10px] text-zinc-400 font-medium italic">
              Supports .json files exported from other projects.
            </p>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-zinc-100">
          <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-6">
            Recently Deleted Projects
          </h2>
          <div className="max-w-2xl space-y-3">
            {!deletedChannels || deletedChannels.length === 0 ? (
              <p className="text-xs text-zinc-400 italic font-medium text-left">
                No projects in trash.
              </p>
            ) : (
              deletedChannels.map((ch) => (
                <div
                  key={ch.id}
                  className="flex items-center justify-between bg-white p-3 rounded-2xl border border-zinc-100 shadow-sm group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-zinc-50 text-zinc-400 flex-shrink-0">
                      <Clock size={16} />
                    </div>
                    <div className="flex flex-col min-w-0 text-left">
                      <span className="text-sm font-bold text-zinc-800 truncate uppercase tracking-tight">
                        {ch.name}
                      </span>
                      <span className="text-[10px] text-zinc-400 font-medium">
                        Deleted{' '}
                        {ch.deletedAt
                          ? new Date(ch.deletedAt.seconds * 1000).toLocaleDateString()
                          : 'recently'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleRestoreChannel(ch)}
                      className="p-2 text-zinc-400 hover:text-green-600 hover:bg-green-50 rounded-xl transition-all active:scale-90"
                      title="Restore Project"
                    >
                      <Undo size={18} />
                    </button>
                    <button
                      onClick={() => handlePurgeChannel(ch.id)}
                      className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all active:scale-90"
                      title="Delete Permanently"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-zinc-100">
          <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-6">Integrations</h2>
          
          <div className="flex flex-col gap-4 max-w-md">
             <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm flex flex-col gap-3">
               <div className="flex items-center gap-3">
                 <Calendar className="text-blue-600" size={24} />
                 <div className="flex-1">
                   <h3 className="text-sm font-bold text-zinc-800">Google Calendar</h3>
                   <p className="text-[10px] text-zinc-500 font-medium leading-snug">View your Google Calendar events alongside your NYTM schedule.</p>
                 </div>
               </div>
               
                {profile?.googleCalendarConnected ? (
                 <>
                   <div className="flex items-center justify-between mt-2 pt-3 border-t border-zinc-100 mb-4">
                      <div className="flex flex-col">
                        <span className="text-[10px] text-green-600 font-bold uppercase tracking-wider flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-green-500" /> Connected</span>
                        {profile?.googleCalendarEmail && <span className="text-[10px] text-zinc-500 font-medium ml-2.5">{profile.googleCalendarEmail}</span>}
                      </div>
                      <button
                        onClick={async () => {
                          if (window.confirm("Disconnect Google Calendar?")) {
                            saveUserProfile({ googleCalendarConnected: false, googleCalendarRefreshToken: null, googleCalendarEmail: null });
                          }
                        }}
                        className="text-[10px] font-bold text-red-500 uppercase tracking-widest hover:text-red-700 transition-colors"
                      >
                        Disconnect
                      </button>
                   </div>
                   
                   {/* Render list of calendars */}
                   {googleCalendars && googleCalendars.length > 0 ? (
                     <div className="flex flex-col gap-2 border-t border-zinc-100 pt-3">
                       <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider mb-1">Select Calendars to Sync</span>
                       {googleCalendars.map(gcal => {
                         const isChecked = profile?.googleCalendarSelectedIds 
                           ? profile.googleCalendarSelectedIds.includes(gcal.id)
                           : gcal.selected;
                           
                         return (
                           <div key={gcal.id} className="flex items-center gap-2 group cursor-pointer p-1 -mx-1 hover:bg-zinc-50 rounded-lg transition-colors" onClick={async (e) => {
                             e.stopPropagation();
                             const currentSelected = profile?.googleCalendarSelectedIds || googleCalendars.filter(c => c.selected).map(c => c.id);
                             let newSelected;
                             if (currentSelected.includes(gcal.id)) {
                               newSelected = currentSelected.filter(id => id !== gcal.id);
                             } else {
                               newSelected = [...currentSelected, gcal.id];
                             }
                             if (saveUserProfile) {
                               await saveUserProfile({ googleCalendarSelectedIds: newSelected });
                             }
                           }}>
                             <div 
                               className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${isChecked ? 'border-transparent' : 'border-zinc-300 bg-white'}`}
                               style={{ backgroundColor: isChecked ? gcal.backgroundColor : undefined }}
                             >
                               {isChecked && <Check size={12} strokeWidth={4} className="text-white" />}
                             </div>
                             <span className="text-xs text-zinc-600 font-medium truncate flex-1 group-hover:text-zinc-900 transition-colors">
                               {gcal.summary}
                             </span>
                           </div>
                         );
                       })}
                     </div>
                   ) : (
                     <div className="text-[10px] text-zinc-500 italic border-t border-zinc-100 pt-3">
                       No calendars found or still loading...
                     </div>
                   )}
                 </>
               ) : (
                 <button
                   onClick={async () => {
                     try {
                        const provider = new GoogleAuthProvider();
                        provider.addScope('https://www.googleapis.com/auth/calendar.readonly');
                        provider.setCustomParameters({
                          prompt: 'consent',
                          access_type: 'offline'
                        });
                        
                        let result;
                        const isGoogleLinked = auth.currentUser.providerData.some(p => p.providerId === 'google.com');
                        
                        if (isGoogleLinked) {
                          result = await signInWithPopup(auth, provider);
                        } else {
                          try {
                            result = await linkWithPopup(auth.currentUser, provider);
                          } catch (error) {
                            if (error.code === 'auth/credential-already-in-use') {
                              throw new Error("This Google account is already connected to another Project Project user. Please try a different Google account.");
                            } else if (error.code === 'auth/provider-already-linked') {
                              result = await signInWithPopup(auth, provider);
                            } else {
                              throw error;
                            }
                          }
                        }
                        
                        const credential = GoogleAuthProvider.credentialFromResult(result);
                        const accessToken = credential?.accessToken || result._tokenResponse?.oauthAccessToken;
                        const refreshToken = result._tokenResponse?.oauthRefreshToken; // Do NOT fallback to result._tokenResponse?.refreshToken (that is the Firebase STS token)
                        if (refreshToken || accessToken) {
                           const connectedEmail = result._tokenResponse?.email || result.user?.email || "your Google account";
                           // 1. Save connection status to public profile
                           await saveUserProfile({ googleCalendarConnected: true, googleCalendarEmail: connectedEmail });
                           // 2. Save tokens securely to private user doc
                           const { doc, setDoc } = await import('firebase/firestore');
                           const { appId: APP_ID_CONSTANT } = await import('../constants');
                           
                           const payload = {
                              updatedAt: new Date().toISOString()
                           };
                           if (refreshToken) payload.refreshToken = refreshToken;
                           if (accessToken) {
                              payload.accessToken = accessToken;
                              payload.tokenExpiresAt = Date.now() + 3500 * 1000;
                           }

                           await safeSetDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'private_users', auth.currentUser.uid, 'integrations', 'google_calendar'), payload, { merge: true });
                           alert(`Google Calendar successfully connected for ${connectedEmail}! You can now right-click the Google Calendar icon on the calendar view to select which calendars to display.`);
                        } else {
                           alert("Connected, but couldn't get access tokens. It may not stay connected.");
                        }
                     } catch (e) {
                        console.error(e);
                        if (e.code === 'auth/popup-blocked') {
                           alert("Popup blocked! Please allow popups for this site in your browser settings and try again.");
                        } else {
                           alert("Failed to connect Google Calendar: " + e.message);
                        }
                     }
                   }}
                   className="mt-2 w-full py-2 bg-blue-50 text-blue-600 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-blue-100 transition-colors"
                 >
                   Connect Google Calendar
                 </button>
               )}
             </div>
          </div>
        </div>

        {/* Exported Calendars Section */}
        <div id="exported-calendars-section" className="mt-12 pt-8 border-t border-zinc-100">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400">
                Exported Calendars
              </h2>
              <p className="text-[10px] text-zinc-400 mt-1 font-medium">
                Manage your active calendar feeds for Google, Outlook, or Apple.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {Object.values(profile?.exportedCalendars || {}).length === 0 ? (
              <p className="text-[10px] text-zinc-300 italic font-medium">
                No active calendar exports. Use the 🌴 emoji in the sidebar to create one.
              </p>
            ) : (
              Object.values(profile?.exportedCalendars || {}).map((feed) => {
                const url = `https://calendarfeed-ipldpbicfq-uc.a.run.app?uid=${user.uid}&feed=${feed.id}&token=${feed.token}`;
                
                const calendarOptions = [
                  { id: 'nytm-event', label: 'Events', icon: Calendar, color: 'text-blue-500' },
                  { id: 'nytm-tentative', label: 'Tentative', icon: HelpCircle, color: 'text-indigo-500' },
                  { id: 'nytm-tasks-starred', label: 'Project Stars', icon: Star, color: 'text-yellow-500' },
                  { id: 'nytm-tasks-h', label: 'Tasks (Hi)', icon: ListTodo, color: 'text-green-600' },
                  { id: 'nytm-tasks-m', label: 'Tasks (Med)', icon: ListTodo, color: 'text-green-400' },
                  { id: 'nytm-tasks-l', label: 'Tasks (Low)', icon: ListTodo, color: 'text-green-200' },
                  { id: 'nytm-flight', label: 'Flights', icon: Plane, color: 'text-sky-500' },
                  { id: 'nytm-hotel', label: 'Hotels', icon: Bed, color: 'text-indigo-500' },
                ];

                const handleDeleteFeed = async () => {
                  if (window.confirm(`Are you sure you want to delete "${feed.name}"?`)) {
                    const nextFeeds = { ...profile.exportedCalendars };
                    delete nextFeeds[feed.id];
                    await saveUserProfile({ exportedCalendars: nextFeeds });
                  }
                };

                const updateFeedNote = async (newNote) => {
                  const nextFeeds = {
                    ...profile.exportedCalendars,
                    [feed.id]: { ...feed, note: newNote }
                  };
                  await saveUserProfile({ exportedCalendars: nextFeeds });
                };

                const toggleFeedType = async (typeId) => {
                  const nextTypes = feed.types.includes(typeId)
                    ? feed.types.filter(t => t !== typeId)
                    : [...feed.types, typeId];
                  
                  const nextFeeds = {
                    ...profile.exportedCalendars,
                    [feed.id]: { ...feed, types: nextTypes }
                  };
                  await saveUserProfile({ exportedCalendars: nextFeeds });
                };

                return (
                  <div key={feed.id} className="p-5 bg-zinc-50 rounded-3xl border border-zinc-100 group relative">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex flex-col">
                        <span className="text-xs font-black uppercase tracking-widest text-zinc-900">
                          {feed.name}
                        </span>
                        <input
                          type="text"
                          value={feed.note || ''}
                          onChange={(e) => updateFeedNote(e.target.value)}
                          placeholder="Add a note (who is this for?)"
                          className="text-[9px] text-zinc-500 font-bold bg-transparent border-none outline-none mt-1 p-0 placeholder:italic w-full"
                        />
                        <span className="text-[9px] text-zinc-400 font-mono mt-1 break-all truncate max-w-[250px]">
                          {url}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(url);
                            alert('Calendar URL copied!');
                          }}
                          className="p-2 hover:bg-white rounded-xl text-zinc-400 hover:text-zinc-900 transition-all shadow-sm"
                          title="Copy Link"
                        >
                          <Copy size={16} strokeWidth={2.5} />
                        </button>
                        <button
                          onClick={handleDeleteFeed}
                          className="p-2 hover:bg-red-50 rounded-xl text-zinc-400 hover:text-red-600 transition-all shadow-sm"
                          title="Delete Feed"
                        >
                          <Trash2 size={16} strokeWidth={2.5} />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {calendarOptions.map((opt) => {
                        const isActive = feed.types.includes(opt.id);
                        return (
                          <button
                            key={opt.id}
                            onClick={() => toggleFeedType(opt.id)}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-[8px] font-black uppercase tracking-widest transition-all ${
                              isActive
                                ? 'bg-zinc-900 border-zinc-900 text-white'
                                : 'bg-white border-zinc-200 text-zinc-400 hover:border-zinc-400'
                            }`}
                          >
                            <opt.icon size={10} strokeWidth={isActive ? 3 : 2.5} className={isActive ? 'text-white' : opt.color} />
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <p className="text-[9px] text-zinc-400 mt-4 italic font-medium">
            Note: Changes to feed settings or deleting a feed will take effect immediately on the server, but external apps may take 8-24 hours to refresh.
          </p>
        </div>

        <div className="mt-12 pt-8 border-t border-zinc-100">
          <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-6">
            App Installation
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-xs">
                  iOS
                </div>
                <span className="text-xs font-black uppercase tracking-widest text-zinc-800">
                  Safari
                </span>
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed font-medium">
                Tap the <span className="font-bold text-zinc-900">Share</span> icon (square with
                arrow) and select{' '}
                <span className="font-bold text-zinc-900">&quot;Add to Home Screen&quot;</span>.
              </p>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center text-green-600 font-bold text-xs">
                  AND
                </div>
                <span className="text-xs font-black uppercase tracking-widest text-zinc-800">
                  Chrome
                </span>
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed font-medium">
                Tap the <span className="font-bold text-zinc-900">Three Dots</span> menu and select{' '}
                <span className="font-bold text-zinc-900">&quot;Install App&quot;</span> or
                &quot;Add to Home Screen&quot;.
              </p>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-zinc-50 flex items-center justify-center text-zinc-600 font-bold text-xs">
                  PC
                </div>
                <span className="text-xs font-black uppercase tracking-widest text-zinc-800">
                  Desktop
                </span>
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed font-medium">
                <span className="font-bold text-zinc-900">Chrome/Edge:</span> Click the{' '}
                <span className="font-bold text-zinc-900">Install Icon</span> (monitor/arrow) in the
                address bar.
                <br />
                <br />
                <span className="font-bold text-zinc-900">Safari (Mac):</span> Go to File &gt;{' '}
                <span className="font-bold text-zinc-900">Add to Dock</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfileSettings;
